import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Loader from '@gym-atoms/Loader/Loader';
import Table, { TableProps } from '@gym-molecules/Table/Table';
import Text from '@gym-atoms/Text/Text';
import { API } from 'aws-amplify';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { getSiteLocationProgramAccessCategories } from '@gym-graphql/queries';
import { removeSiteLocationProgramAccessCategory } from '@gym-graphql/mutations';
import {
  GetSiteLocationProgramAccessCategoriesQuery,
  SiteLocationProgramAccessCategory
} from '@gym-src/API';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import { fetchAccessCategories } from '@gym-redux/slices/accessCategorySlice';
import { GymAccessCategoryType } from '@gym-particles/types/AccessCategoryMapping';
import { GymChainProgramAccessCategoryTable } from '../../../particles/types/GymChainProgramAccessCategory';

const toProgramAccessCategoryDetail = (
  accessCategory: SiteLocationProgramAccessCategory
): GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number] => ({
  programAccessCategoryId:
    accessCategory.siteLocationProgramAccessCategoryId &&
    accessCategory.siteLocationProgramAccessCategoryId > 0
      ? accessCategory.siteLocationProgramAccessCategoryId
      : null,
  programId:
    accessCategory.programId && accessCategory.programId > 0 ? accessCategory.programId : null,
  accessCategoryId:
    accessCategory.accessCategoryId && accessCategory.accessCategoryId > 0
      ? accessCategory.accessCategoryId
      : null,
  arxAccessCategoryId:
    accessCategory.arxAccessCategoryId && accessCategory.arxAccessCategoryId !== ''
      ? accessCategory.arxAccessCategoryId
      : null,
  programName:
    accessCategory.programName && accessCategory.programName !== ''
      ? accessCategory.programName
      : null,
  programImportedId:
    accessCategory.programImportedId && accessCategory.programImportedId > 0
      ? accessCategory.programImportedId
      : null,
  scheduleType:
    accessCategory.scheduleType && accessCategory.scheduleType !== ''
      ? accessCategory.scheduleType
      : null
});

const isSameProgramAccessCategoryDetail = (
  current: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number],
  next: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number]
) =>
  current.programAccessCategoryId === next.programAccessCategoryId &&
  current.programId === next.programId;

const appendUniqueProgramAccessCategoryDetail = (
  details: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'],
  detail: GymChainProgramAccessCategoryTable['programAccessCategoryDetails'][number]
) =>
  details.some((current) => isSameProgramAccessCategoryDetail(current, detail))
    ? details
    : [...details, detail];

const hasProgramAccessCategoryDetails = (accessCategory: GymChainProgramAccessCategoryTable) =>
  accessCategory.programAccessCategoryDetails.some(
    (detail) => detail.programAccessCategoryId && detail.programAccessCategoryId > 0
  );

const toProgramAccessCategoryRow = (
  accessCategory: SiteLocationProgramAccessCategory
): GymChainProgramAccessCategoryTable => ({
  id: accessCategory.accessCategoryId || 0,
  siteId: accessCategory.siteId || 0,
  name: accessCategory.name || '',
  gender: accessCategory.gender || '',
  age: accessCategory.age && accessCategory.age > 0 ? accessCategory.age : null,
  schedulesOnly: accessCategory.schedulesOnly ? 'true' : 'false',
  status: accessCategory.status || '',
  linkSortOrder: accessCategory.siteLocationProgramAccessCategoryId ? 1 : 0,
  programAccessCategoryDetails: [toProgramAccessCategoryDetail(accessCategory)]
});

const mapProgramAccessCategories = (
  accessCategories?: SiteLocationProgramAccessCategory[]
): GymChainProgramAccessCategoryTable[] | undefined => {
  if (!accessCategories) {
    return undefined;
  }

  return accessCategories.reduce<GymChainProgramAccessCategoryTable[]>(
    (mappedAccessCategories, accessCategory) => {
      const mappedIndex = mappedAccessCategories.findIndex(
        (mappedAccessCategory) => mappedAccessCategory.id === accessCategory.accessCategoryId
      );

      if (mappedIndex === -1) {
        return [...mappedAccessCategories, toProgramAccessCategoryRow(accessCategory)];
      }

      return mappedAccessCategories.map((mappedAccessCategory, index) =>
        index === mappedIndex
          ? {
              ...mappedAccessCategory,
              linkSortOrder: Math.max(
                mappedAccessCategory.linkSortOrder || 0,
                accessCategory.siteLocationProgramAccessCategoryId ? 1 : 0
              ),
              programAccessCategoryDetails: appendUniqueProgramAccessCategoryDetail(
                mappedAccessCategory.programAccessCategoryDetails,
                toProgramAccessCategoryDetail(accessCategory)
              )
            }
          : mappedAccessCategory
      );
    },
    []
  );
};

const toUnlinkedProgramAccessCategoryRow = (
  accessCategory: GymAccessCategoryType
): GymChainProgramAccessCategoryTable => ({
  id: accessCategory.accessCategoryId,
  siteId: accessCategory.siteId,
  name: accessCategory.name,
  gender: accessCategory.gender,
  age: accessCategory.age > 0 ? accessCategory.age : null,
  schedulesOnly: accessCategory.schedulesOnly,
  status: accessCategory.status,
  linkSortOrder: 0,
  programAccessCategoryDetails: [
    {
      accessCategoryId: accessCategory.accessCategoryId,
      programAccessCategoryId: null,
      arxAccessCategoryId: null,
      programId: null,
      programImportedId: null,
      programName: null,
      scheduleType: null
    }
  ]
});

const includeUnlinkedAccessCategories = (
  programAccessCategories: GymChainProgramAccessCategoryTable[],
  accessCategories: GymAccessCategoryType[] | undefined,
  siteLocationId: number
): GymChainProgramAccessCategoryTable[] => {
  const programLinkedIds = new Set(programAccessCategories.map((row) => row.id));

  const unlinkedAccessCategories =
    accessCategories
      ?.filter(
        (accessCategory) =>
          accessCategory.siteLocationId === siteLocationId &&
          accessCategory.siteLocationMembershipId === 0 &&
          !programLinkedIds.has(accessCategory.accessCategoryId)
      )
      .map(toUnlinkedProgramAccessCategoryRow) || [];

  return [...programAccessCategories, ...unlinkedAccessCategories];
};

const ProgramAccessCategoryMappingList = (props: ProgramAccessCategoryMappingListProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [selectedRow, setSelectedRow] = useState<GymChainProgramAccessCategoryTable>();
  const [selectedRows, setSelectedRows] = useState<GymChainProgramAccessCategoryTable[]>([]);
  const [sortField, setSortField] = useState('linkSortOrder');
  const [sortOrder, setSortOrder] = useState(-1);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [isAccessCategoryLoading, setIsAccessCategoryLoading] = useState(false);
  const [programAccessCategories, setProgramAccessCategories] = useState<
    GymChainProgramAccessCategoryTable[]
  >([]);
  const gymChainName = useAppSelector(
    (state) => state.gymChain.items.find((g) => g.id === props.chainId)?.name
  );
  const locationAccessCategories = useAppSelector(
    (state) => state.accessCategory.items.find((e) => e.siteLocationId === props.gymId)?.items
  );
  const displayedProgramAccessCategories = includeUnlinkedAccessCategories(
    programAccessCategories,
    locationAccessCategories,
    props.gymId
  );

  const fetchProgramAccessCategories = async () => {
    setIsAccessCategoryLoading(true);

    try {
      const response = await (API.graphql({
        query: getSiteLocationProgramAccessCategories,
        variables: {
          siteLocationId: props.gymId
        }
      }) as Promise<{ data: GetSiteLocationProgramAccessCategoriesQuery }>);

      setProgramAccessCategories(
        mapProgramAccessCategories(
          (
            response.data.getSiteLocationProgramAccessCategories?.items || []
          ).filter((item): item is SiteLocationProgramAccessCategory => Boolean(item))
        ) || []
      );
    } finally {
      setIsAccessCategoryLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchAccessCategories({ siteLocationId: props.gymId }));
    fetchProgramAccessCategories();
  }, [props.gymId]);

  const locationRouteState = {
    from: 'gym',
    gymId: props.gymId
  };

  const deleteDialogHandler = async () => {
    const ids = (selectedRows || [])
      .flatMap((row) =>
        row.programAccessCategoryDetails.map((detail) => detail.programAccessCategoryId)
      )
      .filter((id): id is number => Number.isFinite(id));

    if (ids.length > 0) {
      await API.graphql({
        query: removeSiteLocationProgramAccessCategory,
        variables: {
          ids,
          siteLocationId: props.gymId
        }
      });
      await fetchProgramAccessCategories();
    }

    setSelectedRows([]);
    setSelectedRow(undefined);
    setDeleteDialogVisible(false);
  };

  const tablePropsAccessCategory: TableProps<GymChainProgramAccessCategoryTable> = {
    selectionMode: 'checkbox',
    enableChildRowClick: true,
    exportFileName: `GymProgramAccessCategories-${gymChainName}`,
    data: displayedProgramAccessCategories || undefined,
    searchPlaceholderText: t('ACCESS_CATEGORY.SEARCH_FIELD_TEXT'),
    excelBtntext: t('ACCESS_CATEGORY.EXPORT_EXCEL_BUTTON_LABEL'),
    totalRecords: displayedProgramAccessCategories.length,
    selectedRows: selectedRows,
    selectedRow: selectedRow,
    pageSize: pageSize,
    offset: offset,
    setPageSize: setPageSize,
    setOffset: setOffset,
    setSelectedRow: setSelectedRow,
    setSelectedRows: setSelectedRows,
    setSortField: setSortField,
    setSortOrder: setSortOrder,
    sortField: sortField,
    sortOrder: sortOrder,
    emptyStateTexts: [t('ACCESS_CATEGORY.EMPTY_STATE_TEXT')],
    columns: [
      {
        field: 'name',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_NAME'),
        toggable: false,
        sortable: true,
        truncateText: true
      },
      {
        field: 'gender',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_GENDER'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'age',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_AGE'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'schedulesOnly',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULES_ONLY'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'status',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_STATUS'),
        toggable: true,
        truncateText: true
      }
    ],
    isContextMenu: true,
    menuItem: [
      {
        label: t('COMMON.MODIFY'),
        command: () =>
          history.push({
            pathname: `/gymChains/${props.chainId}/gyms/${props.gymId}/gymChainProgramAccessCategories/${selectedRow?.id}`,
            state: {
              ...locationRouteState,
              accessCategoryTab: 'bookings',
              programAccessCategory: selectedRow
            }
          })
      },
      {
        label: t('COMMON.DELETE'),
        command: () => {
          setSelectedRows(selectedRow ? [selectedRow] : []);
          setDeleteDialogVisible(true);
        }
      }
    ],
    lazy: false,
    expandable: true,
    isRowExpandable: hasProgramAccessCategoryDetails,
    expandableField: 'programAccessCategoryDetails',
    expandedCols: [
      {
        field: 'programAccessCategoryId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_ACCESS_CATEGORY_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'arxAccessCategoryId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_ARX_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programImportedId',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_IMPORTED_ID'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'programName',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_PROGRAM_NAME'),
        toggable: true,
        truncateText: true
      },
      {
        field: 'scheduleType',
        header: t('ACCESS_CATEGORY.TABLE.HEADER_ACCESS_CATEGORY_SCHEDULE_TYPE'),
        toggable: true,
        truncateText: true
      }
    ],
    headerBtns: [
      <div className="d-flex" key={1}>
        <Button
          label={t('ACCESS_CATEGORY.ADD_NEW_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() =>
            history.push({
              pathname: `/gymChains/${props.chainId}/gyms/${props.gymId}/gymChainProgramAccessCategories/new`,
              state: {
                ...locationRouteState,
                accessCategoryTab: 'bookings'
              }
            })
          }
        />
      </div>,
      <div className="d-flex" key={2}>
        <Button
          variant={ButtonVariants.outline}
          label={t('ACCESS_CATEGORY.DELETE_SELECTED_ACCESS_CATEGORY_BUTTON_LABEL')}
          size={ButtonSizes.small}
          onClick={() => setDeleteDialogVisible(true)}
          disabled={selectedRows?.length === 0 || false}
        />
      </div>
    ]
  };

  return (
    <>
      <DialogBox
        dialogVisible={deleteDialogVisible}
        variant={DialogBoxVariants.long}
        dialogDismissableMask={true}
        onHideCallback={() => setDeleteDialogVisible(false)}
        dialogFooter={
          <div>
            <Button
              label={t('COMMON.CANCEL')}
              variant={ButtonVariants.textonly}
              onClick={() => setDeleteDialogVisible(false)}
            />
            <Button
              label={t('COMMON.DELETE')}
              variant={ButtonVariants.danger}
              onClick={deleteDialogHandler}
            />
          </div>
        }
        dialogClosable={false}
        dialogHeader={t('COMMON.DELETE')}
        content={
          <Text>
            {t('ACCESS_CATEGORY.DELETE_MODAL_CONFIRM')}{' '}
            <span className="fw-bold">{selectedRows?.map((row) => row.name).join(', ')}</span>?
          </Text>
        }
      />
      {isAccessCategoryLoading ? <Loader shape="table" /> : <Table {...tablePropsAccessCategory} />}
    </>
  );
};

export interface ProgramAccessCategoryMappingListProps {
  gymId: number;
  chainId: number;
}

export default ProgramAccessCategoryMappingList;
